import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button3D } from '@/components/ui/button-3d';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { ContactRound, Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useOfflineSync } from '@/hooks/useOfflineSync';
import { useHaptics } from '@/hooks/useHaptics';
import type { Client } from '@/types/database';
import {
  buildExistingClientIndex,
  contactDisplayName,
  contactToClientColumns,
  dedupeContacts,
  isAlreadyRegistered,
  matchesContactSearch,
  pickEmail,
  pickPhone,
  sortContactsByName,
  type ContactClientColumns,
  type DeviceContact,
} from '@/lib/contactImport';

/**
 * Importar contatos da agenda do celular como clientes (grátis, só no app nativo).
 *
 * Fluxo: pede permissão → lê a agenda → o usuário escolhe quem importar →
 * grava (em lote quando online, na fila offline quando não).
 */

/** Teto de linhas renderizadas: agendas com milhares de contatos travariam a WebView. */
const MAX_VISIBLE = 200;

interface ImportContactsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Clientes já cadastrados, para marcar duplicatas. */
  existingClients: Client[];
  userId: string;
  /** Obrigatório para a RLS de team accounts aceitar o INSERT (CLAUDE.md #4). */
  companyId: string | null;
  /** Chamado após importar, para a página recarregar a lista. */
  onImported: () => void;
}

interface ContactRow {
  key: string;
  contact: DeviceContact;
  name: string;
  phone: string | null;
  email: string | null;
  registered: boolean;
  columns: ContactClientColumns;
}

type LoadState = 'idle' | 'loading' | 'ready' | 'denied' | 'failed';

export function ImportContactsModal({
  open,
  onOpenChange,
  existingClients,
  userId,
  companyId,
  onImported,
}: ImportContactsModalProps) {
  const { checkOnline, saveClientOffline } = useOfflineSync();
  const { successFeedback } = useHaptics();

  const [state, setState] = useState<LoadState>('idle');
  const [contacts, setContacts] = useState<DeviceContact[]>([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [importing, setImporting] = useState(false);
  // Evita repetir o aviso de "todos já cadastrados" a cada re-render.
  const warnedAllRegistered = useRef(false);

  const loadContacts = useCallback(async () => {
    setState('loading');
    // Zera busca e seleção: no "Tentar novamente" as chaves antigas poderiam
    // sobreviver e casar com outros contatos da releitura.
    setSearch('');
    setSelected(new Set());
    try {
      const { Contacts } = await import('@capacitor-community/contacts');

      // A leitura EXIGE a permissão, então pedimos — mas não usamos o status
      // como portão: logo depois de conceder, o agregado devolvido por
      // requestPermissions() vem defasado e daria um falso "sem permissão"
      // (mesmo motivo do "Salvar na agenda" em QuoteForm). Tentamos ler assim
      // mesmo e só tratamos como negada se a leitura falhar por permissão.
      try {
        await Contacts.requestPermissions();
      } catch {
        // Ignorado de propósito: o getContacts abaixo é quem decide.
      }

      const { contacts: raw } = await Contacts.getContacts({
        projection: { name: true, phones: true, emails: true },
      });

      const list = sortContactsByName(dedupeContacts((raw ?? []) as DeviceContact[]));
      setContacts(list);
      setState('ready');

      if (list.length === 0) {
        toast.info('Nenhum contato encontrado na agenda do celular.');
      }
    } catch (error: unknown) {
      const message = (error as { message?: string })?.message || '';
      console.error('[ImportContactsModal] Erro ao ler contatos:', error);
      if (/permission|denied/i.test(message)) {
        setState('denied');
        toast.warning(
          'Permissão de contatos negada. Vá em Configurações > Apps > Orçamigo > Permissões.',
        );
      } else {
        setState('failed');
        toast.error('Erro ao ler a agenda. Tente novamente.');
      }
    }
  }, []);

  // Lê a agenda quando o modal abre (o usuário já pediu isso ao tocar em
  // "Importar"); ao fechar, zera o estado para a próxima abertura vir limpa.
  useEffect(() => {
    if (!open) {
      setState('idle');
      setContacts([]);
      setSearch('');
      setSelected(new Set());
      warnedAllRegistered.current = false;
      return;
    }
    loadContacts();
  }, [open, loadContacts]);

  const rows = useMemo<ContactRow[]>(() => {
    const index = buildExistingClientIndex(existingClients);
    return contacts.flatMap((contact, i) => {
      const columns = contactToClientColumns(contact);
      if (!columns) return [];
      return [
        {
          key: contact.contactId || `contact_${i}`,
          contact,
          name: contactDisplayName(contact),
          phone: pickPhone(contact),
          email: pickEmail(contact),
          registered: isAlreadyRegistered(contact, index),
          columns,
        },
      ];
    });
  }, [contacts, existingClients]);

  // Agenda inteira já na base: avisa uma vez, senão o usuário fica olhando uma
  // lista toda desabilitada sem entender o porquê.
  useEffect(() => {
    if (state !== 'ready' || rows.length === 0 || warnedAllRegistered.current) return;
    if (rows.every((r) => r.registered)) {
      warnedAllRegistered.current = true;
      toast.warning('Todos os contatos da agenda já estão cadastrados como clientes.');
    }
  }, [state, rows]);

  const filtered = useMemo(
    () => rows.filter((r) => matchesContactSearch(r.contact, search)),
    [rows, search],
  );
  const importable = useMemo(() => filtered.filter((r) => !r.registered), [filtered]);
  const visible = filtered.slice(0, MAX_VISIBLE);

  const allFilteredSelected =
    importable.length > 0 && importable.every((r) => selected.has(r.key));

  const toggle = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleAllFiltered = (checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const r of importable) {
        if (checked) next.add(r.key);
        else next.delete(r.key);
      }
      return next;
    });
  };

  const handleImport = async () => {
    const chosen = rows.filter((r) => selected.has(r.key) && !r.registered);

    if (chosen.length === 0) {
      toast.warning('Selecione ao menos um contato para importar.');
      return;
    }

    setImporting(true);

    const online = await checkOnline();

    if (!online) {
      // Offline: cada contato entra na fila com um id temporário. `silent` evita
      // um toast por contato — damos um único resumo abaixo.
      for (const row of chosen) {
        saveClientOffline(
          { client: row.columns, userId, companyId },
          { silent: true },
        );
      }
      setImporting(false);
      successFeedback();
      onOpenChange(false);
      onImported();
      toast.success(
        `${chosen.length} contato${chosen.length !== 1 ? 's' : ''} salvo${chosen.length !== 1 ? 's' : ''} localmente! Será sincronizado quando houver internet.`,
        { icon: '📴' },
      );
      return;
    }

    const { error } = await supabase.from('clients').insert(
      chosen.map((row) => ({
        user_id: userId,
        company_id: companyId,
        ...row.columns,
      })),
    );

    setImporting(false);

    if (error) {
      console.error('[ImportContactsModal] Erro ao importar contatos:', error);
      toast.error('Erro ao importar contatos. Tente novamente.');
      return;
    }

    successFeedback();
    onOpenChange(false);
    onImported();
    toast.success(
      `${chosen.length} contato${chosen.length !== 1 ? 's' : ''} importado${chosen.length !== 1 ? 's' : ''}!`,
    );
  };

  const selectedCount = rows.filter((r) => selected.has(r.key) && !r.registered).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ContactRound className="h-5 w-5" />
            Importar da agenda
          </DialogTitle>
          <DialogDescription>
            Escolha os contatos do celular que você quer cadastrar como clientes.
          </DialogDescription>
        </DialogHeader>

        {state === 'loading' && (
          <div className="flex flex-col items-center justify-center gap-3 py-10 text-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Lendo a agenda do celular...</p>
          </div>
        )}

        {(state === 'denied' || state === 'failed') && (
          <div className="flex flex-col items-center justify-center gap-3 py-10 text-center">
            <ContactRound className="h-10 w-10 text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">
              {state === 'denied'
                ? 'O app não tem permissão para ler seus contatos. Libere em Configurações > Apps > Orçamigo > Permissões.'
                : 'Não foi possível ler a agenda do celular.'}
            </p>
            <Button3D size="sm" color="gray" onClick={loadContacts}>
              Tentar novamente
            </Button3D>
          </div>
        )}

        {state === 'ready' && rows.length === 0 && (
          <div className="flex flex-col items-center justify-center gap-3 py-10 text-center">
            <ContactRound className="h-10 w-10 text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">
              Nenhum contato com nome foi encontrado na agenda.
            </p>
          </div>
        )}

        {state === 'ready' && rows.length > 0 && (
          <>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Buscar contato..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>

            <div className="flex items-center gap-2 border-b px-1 py-2">
              <Checkbox
                checked={allFilteredSelected}
                disabled={importable.length === 0}
                onCheckedChange={(checked) => toggleAllFiltered(!!checked)}
                aria-label="Selecionar todos os contatos do filtro"
              />
              <span className="text-sm text-muted-foreground">
                Selecionar todos ({importable.length})
              </span>
            </div>

            <div className="-mx-1 flex-1 overflow-y-auto px-1">
              {visible.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  Nenhum contato encontrado
                </p>
              ) : (
                <ul className="divide-y">
                  {visible.map((row) => (
                    <li key={row.key}>
                      {/* Mesmo padrão dos cards de cliente: o Checkbox cuida do
                          próprio clique e a área de texto tem o seu — envolver
                          num <label> dispararia o toggle duas vezes. */}
                      <div
                        className={`flex items-center gap-3 py-3 ${
                          row.registered ? 'opacity-50' : ''
                        }`}
                      >
                        <Checkbox
                          checked={selected.has(row.key)}
                          disabled={row.registered}
                          onCheckedChange={() => toggle(row.key)}
                          onClick={(e) => e.stopPropagation()}
                          aria-label={`Selecionar ${row.name}`}
                        />
                        <div
                          className={`min-w-0 flex-1 ${row.registered ? '' : 'cursor-pointer'}`}
                          onClick={() => {
                            if (!row.registered) toggle(row.key);
                          }}
                        >
                          <p className="truncate text-sm font-medium">{row.name}</p>
                          <p className="truncate text-xs text-muted-foreground">
                            {row.registered
                              ? 'Já cadastrado'
                              : [row.phone, row.email].filter(Boolean).join(' · ') ||
                                'Sem telefone'}
                          </p>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              {filtered.length > visible.length && (
                <p className="py-3 text-center text-xs text-muted-foreground">
                  Mostrando {visible.length} de {filtered.length}. Use a busca para refinar.
                </p>
              )}
            </div>
          </>
        )}

        <DialogFooter className="mt-2">
          <Button3D
            className="w-full"
            onClick={handleImport}
            disabled={importing || state !== 'ready' || selectedCount === 0}
          >
            {importing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Importando...
              </>
            ) : selectedCount === 0 ? (
              'Importar contatos'
            ) : (
              `Importar ${selectedCount} contato${selectedCount !== 1 ? 's' : ''}`
            )}
          </Button3D>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
